import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LocalRepository, type UploadInput } from "./localRepository";
import {
  confirmCognitoSignUp,
  resendCognitoConfirmationCode,
  signUpWithCognito,
} from "./cognitoAuth";
import type { AppState, CompanyRole, ShareLinkType, Upload } from "../types/domain";

interface SessionContextValue {
  state: AppState;
  repo: LocalRepository;
  refresh: () => void;
  signIn: (email: string, password: string) => void;
  signOut: () => void;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
  resendVerificationCode: () => Promise<void>;
  requestPasswordReset: (email: string) => void;
  createCompany: (name: string, userName?: string) => void;
  createUpload: (input: UploadInput) => Promise<Upload>;
  updateUpload: (uploadId: string, patch: Partial<Upload>) => void;
  deleteUpload: (uploadId: string) => void;
  createShareLink: (uploadId: string, type: ShareLinkType, hideSerialNumbers?: boolean) => void;
  disableShareLink: (shareId: string) => void;
  inviteMember: (companyId: string, email: string, role: CompanyRole) => void;
  updateMember: (
    companyId: string,
    userId: string,
    patch: { role?: CompanyRole; status?: "ACTIVE" | "INVITED" | "DISABLED" },
  ) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [repo] = useState(() => new LocalRepository());
  const [state, setState] = useState(() => repo.snapshot());
  const refresh = useCallback(() => setState(repo.snapshot()), [repo]);

  useEffect(() => {
    let isActive = true;
    repo.ready.then(() => {
      if (isActive) refresh();
    });
    return () => {
      isActive = false;
    };
  }, [refresh, repo]);

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      repo,
      refresh,
      signIn(email, password) {
        repo.signIn(email, password);
        refresh();
      },
      signOut() {
        repo.signOut();
        refresh();
      },
      async signUp(email, password, name) {
        await signUpWithCognito(email, password, name);
        repo.signUp(email, password, name);
        refresh();
      },
      async verifyEmail(code) {
        const user = repo.currentUser();
        if (!user) throw new Error("Create an account before verifying your email.");
        await confirmCognitoSignUp(user.email, code);
        repo.verifyEmail();
        refresh();
      },
      async resendVerificationCode() {
        const user = repo.currentUser();
        if (!user) throw new Error("Create an account before requesting a verification code.");
        await resendCognitoConfirmationCode(user.email);
      },
      requestPasswordReset(email) {
        repo.requestPasswordReset(email);
      },
      createCompany(name, userName) {
        repo.createCompany(name, userName);
        refresh();
      },
      async createUpload(input) {
        const upload = await repo.createUpload(input);
        refresh();
        return upload;
      },
      updateUpload(uploadId, patch) {
        repo.updateUpload(uploadId, patch);
        refresh();
      },
      deleteUpload(uploadId) {
        repo.deleteUpload(uploadId);
        refresh();
      },
      createShareLink(uploadId, type, hideSerialNumbers) {
        repo.createShareLink({ uploadId, type, hideSerialNumbers });
        refresh();
      },
      disableShareLink(shareId) {
        repo.disableShareLink(shareId);
        refresh();
      },
      inviteMember(companyId, email, role) {
        repo.inviteMember(companyId, email, role);
        refresh();
      },
      updateMember(companyId, userId, patch) {
        repo.updateMember(companyId, userId, patch);
        refresh();
      },
    }),
    [refresh, repo, state],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

export function useCurrentUser() {
  const { state } = useSession();
  return state.users.find((user) => user.userId === state.currentUserId);
}

export function useCurrentCompany() {
  const { state } = useSession();
  return state.companies.find((company) => company.companyId === state.currentCompanyId);
}
